import React, { useState } from 'react';
import { Plane, Calendar, MapPin, User, Mail, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { airports } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FlightBookingForm = () => {
  const [tripType, setTripType] = useState('oneway');
  const [purpose, setPurpose] = useState('visa');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [showPrice, setShowPrice] = useState(false);

  // Contact details
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handlePurposeChange = (value) => {
    setPurpose(value);
    if (value === 'immigration') setTripType('roundtrip');
  };

  const validateRoute = () => {
    if (!origin || !destination || !departDate) {
      setError('Please fill in all route fields');
      return false;
    }
    if (origin === destination) {
      setError('Origin and destination cannot be the same');
      return false;
    }
    if (tripType === 'roundtrip' && !returnDate) {
      setError('Please select a return date');
      return false;
    }
    setError('');
    return true;
  };

  const handleShowPrice = () => {
    if (!validateRoute()) return;
    setShowPrice(true);
  };

  const getPriceDetails = () => {
    if (purpose === 'visa') {
      return { price: '999', validity: '3-7 days', description: 'Valid for visa application purposes' };
    }
    return { price: '4,800', validity: 'Until return flight date', description: 'Valid for immigration with confirmed return flight' };
  };

  const priceDetails = getPriceDetails();

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      setError('Please enter your name and phone number');
      return;
    }
    setError('');
    setSubmitting(true);

    const selectedOrigin = airports.find((a) => a.iata === origin);
    const selectedDest = airports.find((a) => a.iata === destination);

    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      trip_type: tripType,
      purpose,
      origin_iata: origin,
      origin_city: selectedOrigin?.city || '',
      destination_iata: destination,
      destination_city: selectedDest?.city || '',
      departure_date: departDate,
      return_date: tripType === 'roundtrip' ? returnDate : '',
      price: priceDetails.price,
      notes: '',
    };

    try {
      const res = await fetch(`${API}/submissions/flight`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Submission failed');
      await res.json();
      setSubmitted(true);

      // GA4 conversion event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'flight_form_submit', {
          purpose,
          trip_type: tripType,
          origin,
          destination,
          price: priceDetails.price,
        });
      }

      // Auto-open WhatsApp with prefilled message after a short delay
      setTimeout(() => {
        const message =
          `Hello, I just submitted a flight reservation request:\n\n` +
          `Name: ${name}\n` +
          `Trip: ${tripType === 'oneway' ? 'One-way' : 'Round-trip'} (${purpose === 'visa' ? 'Visa' : 'Immigration'})\n` +
          `Route: ${selectedOrigin?.city} (${origin}) → ${selectedDest?.city} (${destination})\n` +
          `Departure: ${departDate}` +
          (tripType === 'roundtrip' ? `\nReturn: ${returnDate}` : '') +
          `\nPrice: ₹${priceDetails.price}`;
        window.open(
          `https://wa.me/919353536633?text=${encodeURIComponent(message)}`,
          '_blank'
        );
      }, 800);
    } catch (e) {
      setError('Could not submit. Please try again or contact us on WhatsApp directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setShowPrice(false);
    setName('');
    setEmail('');
    setPhone('');
    setOrigin('');
    setDestination('');
    setDepartDate('');
    setReturnDate('');
  };

  return (
    <section id="booking" className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-orange-50/30 via-white to-blue-50/30 pointer-events-none"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-orange-100 text-blue-800 rounded-full text-sm font-medium mb-4">
            <Plane size={16} className="mr-2" />
            Quick & Easy Booking
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">
            Book Your{' '}
            <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Flight Reservation
            </span>
          </h2>
          <p className="text-lg text-gray-600">Select your route and get instant pricing</p>
        </div>

        {submitted ? (
          <Card className="shadow-2xl border-2 border-green-200" data-testid="success-card">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircle2 className="text-white" size={48} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Submission Received!</h3>
              <p className="text-gray-600 mb-2">
                Thank you, <span className="font-semibold">{name}</span>!
              </p>
              <p className="text-gray-600 mb-6">
                We've received your booking request and our team will contact you on WhatsApp within minutes.
              </p>
              <div className="bg-green-50 rounded-lg p-4 mb-6 inline-block">
                <p className="text-sm text-green-800">
                  📱 Opening WhatsApp to continue your booking...
                </p>
              </div>
              <div>
                <Button
                  onClick={resetForm}
                  variant="outline"
                  className="border-gray-300"
                  data-testid="new-booking-btn"
                >
                  Make Another Booking
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-2xl border-0 overflow-hidden" data-testid="booking-card">
            <CardHeader className="bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 text-white">
              <CardTitle className="flex items-center text-2xl">
                <Plane className="mr-2" size={28} />
                Route Selection
              </CardTitle>
              <CardDescription className="text-blue-100">
                Choose your travel details
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8 space-y-6">
              {/* Trip Type */}
              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">Trip Type</Label>
                <RadioGroup value={tripType} onValueChange={setTripType}>
                  <div className="grid grid-cols-2 gap-3">
                    <label
                      htmlFor="oneway"
                      className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        tripType === 'oneway'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      } ${purpose === 'immigration' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <RadioGroupItem value="oneway" id="oneway" disabled={purpose === 'immigration'} data-testid="trip-oneway" />
                      <span className="font-medium">One-way</span>
                    </label>
                    <label
                      htmlFor="roundtrip"
                      className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        tripType === 'roundtrip'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <RadioGroupItem value="roundtrip" id="roundtrip" data-testid="trip-roundtrip" />
                      <span className="font-medium">Round-trip</span>
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Purpose */}
              <div className="space-y-3">
                <Label className="text-base font-semibold text-gray-800">Purpose</Label>
                <RadioGroup value={purpose} onValueChange={handlePurposeChange}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label
                      htmlFor="visa"
                      className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        purpose === 'visa'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-300'
                      }`}
                    >
                      <RadioGroupItem value="visa" id="visa" data-testid="purpose-visa" />
                      <div>
                        <div className="font-medium">Visa Application</div>
                        <div className="text-xs text-gray-500">₹999 • Valid 3-7 days</div>
                      </div>
                    </label>
                    <label
                      htmlFor="immigration"
                      className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        purpose === 'immigration'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <RadioGroupItem value="immigration" id="immigration" data-testid="purpose-immigration" />
                      <div>
                        <div className="font-medium">Immigration</div>
                        <div className="text-xs text-gray-500">₹4,800 • Until return date</div>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Origin and Destination */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="origin" className="text-base font-semibold text-gray-800 flex items-center">
                    <MapPin size={16} className="mr-1 text-blue-600" />
                    From
                  </Label>
                  <Select value={origin} onValueChange={setOrigin}>
                    <SelectTrigger id="origin" className="h-12" data-testid="origin-select">
                      <SelectValue placeholder="Select origin airport" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {airports.map((airport) => (
                        <SelectItem key={airport.iata} value={airport.iata}>
                          {airport.city} ({airport.iata}) - {airport.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="destination" className="text-base font-semibold text-gray-800 flex items-center">
                    <MapPin size={16} className="mr-1 text-orange-600" />
                    To
                  </Label>
                  <Select value={destination} onValueChange={setDestination}>
                    <SelectTrigger id="destination" className="h-12" data-testid="destination-select">
                      <SelectValue placeholder="Select destination airport" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {airports.map((airport) => (
                        <SelectItem key={airport.iata} value={airport.iata}>
                          {airport.city} ({airport.iata}) - {airport.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="departDate" className="text-base font-semibold text-gray-800 flex items-center">
                    <Calendar size={16} className="mr-1 text-blue-600" />
                    Departure Date
                  </Label>
                  <input
                    type="date"
                    id="departDate"
                    value={departDate}
                    onChange={(e) => setDepartDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="departure-date"
                    className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {tripType === 'roundtrip' && (
                  <div className="space-y-2">
                    <Label htmlFor="returnDate" className="text-base font-semibold text-gray-800 flex items-center">
                      <Calendar size={16} className="mr-1 text-orange-600" />
                      Return Date
                    </Label>
                    <input
                      type="date"
                      id="returnDate"
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      min={departDate || new Date().toISOString().split('T')[0]}
                      data-testid="return-date"
                      className="w-full h-12 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Show Price Button */}
              {!showPrice && (
                <Button
                  onClick={handleShowPrice}
                  data-testid="show-price-btn"
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg font-semibold rounded-xl shadow-lg"
                >
                  Show Price
                </Button>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm" data-testid="error-msg">
                  {error}
                </div>
              )}

              {/* Price Display & Contact Form */}
              {showPrice && (
                <div className="space-y-6">
                  <div className="p-6 bg-gradient-to-br from-blue-50 via-purple-50 to-orange-50 rounded-2xl border-2 border-blue-200" data-testid="price-display">
                    <div className="text-center mb-2">
                      <p className="text-sm text-gray-600 uppercase tracking-wide mb-2 font-semibold">
                        {purpose === 'visa' ? 'Visa Purposes' : 'Immigration'} • {tripType === 'oneway' ? 'One-way' : 'Round-trip'}
                      </p>
                      <p className="text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                        ₹{priceDetails.price}
                      </p>
                      <p className="text-sm text-gray-600">Valid for {priceDetails.validity}</p>
                      <p className="text-xs text-gray-500 mt-1">{priceDetails.description}</p>
                    </div>
                  </div>

                  {/* Contact Details */}
                  <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Your Contact Details</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      We'll reach out on WhatsApp to confirm your booking
                    </p>

                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-semibold flex items-center">
                        <User size={14} className="mr-1 text-blue-600" />
                        Full Name <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your full name"
                        data-testid="name-input"
                        className="h-11"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-sm font-semibold flex items-center">
                          <Phone size={14} className="mr-1 text-blue-600" />
                          Phone (WhatsApp) <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+91 9876543210"
                          data-testid="phone-input"
                          className="h-11"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-semibold flex items-center">
                          <Mail size={14} className="mr-1 text-blue-600" />
                          Email <span className="text-gray-400 ml-1">(optional)</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          data-testid="email-input"
                          className="h-11"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      data-testid="submit-btn"
                      className="w-full h-12 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#20BA5A] hover:to-[#0e6e63] text-white font-semibold rounded-xl shadow-lg disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={20} />
                          Submitting...
                        </>
                      ) : (
                        'Submit & Continue on WhatsApp'
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to be contacted regarding your booking.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};
